
import { useState, useTransition } from 'react';
import { Link } from 'react-router-dom';
import {
  useUser,
  useFirestore,
  useCollection,
  useMemoFirebase,
  updateDocumentNonBlocking,
} from '@/firebase';
import { collection, query, orderBy, doc } from 'firebase/firestore';
import type { Itinerary, Loch } from '@/app/lib/types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { LogIn, PlusCircle, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface AddToItineraryProps {
  location: Loch;
}

export function AddToItinerary({ location }: AddToItineraryProps) {
  const { user, isUserLoading } = useUser();
  const firestore = useFirestore();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [selectedItineraryId, setSelectedItineraryId] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSaving, startSavingTransition] = useTransition();

  const itinerariesQuery = useMemoFirebase(() => {
    if (!user) return null;
    return query(
      collection(firestore, 'users', user.uid, 'itineraries'),
      orderBy('startDate', 'desc')
    );
  }, [user, firestore]);

  const { data: itineraries, isLoading: itinerariesLoading } =
    useCollection<Itinerary>(itinerariesQuery);

  const selectedItinerary = itineraries?.find(it => it.id === selectedItineraryId);
  
  const handleSave = () => {
    if (!user || !selectedItineraryId || !selectedDay || !selectedItinerary) {
      toast({
        variant: 'destructive',
        title: 'Selection missing',
        description: 'Please select both an itinerary and a day.',
      });
      return;
    }
    
    startSavingTransition(() => {
        const itineraryRef = doc(firestore, 'users', user.uid, 'itineraries', selectedItineraryId);

        const newActivity = {
            time: 'Afternoon',
            description: `A swim at ${location.name}. ${location.description}`,
            locationName: location.name,
            locationSlug: location.slug,
            category: 'swim' as const,
            coordinates: { lat: location.Latitude, lng: location.Longitude }
        };

        const updatedDays = selectedItinerary.generatedItinerary.days.map(day => {
            if (day.day === selectedDay) {
                return {
                    ...day,
                    activities: [...day.activities, newActivity]
                };
            }
            return day;
        });

        const updatedGeneratedItinerary = {
            ...selectedItinerary.generatedItinerary,
            days: updatedDays,
        };

        updateDocumentNonBlocking(itineraryRef, {
            generatedItinerary: updatedGeneratedItinerary
        });

        toast({
            title: "Location Added!",
            description: `${location.name} has been added to Day ${selectedDay} of your itinerary.`,
            action: (
              <Button asChild variant="outline" size="sm">
                <Link to={`/itinerary/${selectedItineraryId}`}>View Itinerary</Link>
              </Button>
            ),
        });
        setIsDialogOpen(false);
        setSelectedItineraryId(null);
        setSelectedDay(null);
    });
  };

  if (isUserLoading) {
    return <p className="text-sm text-muted-foreground">Loading user...</p>;
  }

  if (!user) {
    return (
      <div className="flex flex-col items-center text-center gap-4">
        <p className="text-sm text-muted-foreground">
          Log in to add this spot to one of your itineraries.
        </p>
        <Button asChild variant="outline" className="w-full">
          <Link to={`/login?redirect_to=/locations/${location.slug}`}>
            <LogIn className="mr-2 h-4 w-4" /> Log In
          </Link>
        </Button>
      </div>
    );
  }
  
  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button className="w-full">
            <PlusCircle className="mr-2 h-4 w-4" />
            Add to an Itinerary
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add "{location.name}" to...</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Itinerary</label>
            <Select
                value={selectedItineraryId || ''}
                onValueChange={id => {
                    setSelectedItineraryId(id);
                    setSelectedDay(null); // Reset day selection when itinerary changes
                }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select an itinerary..." />
              </SelectTrigger>
              <SelectContent>
                {itinerariesLoading && <SelectItem value="loading" disabled>Loading...</SelectItem>}
                {itineraries && itineraries.map(it => (
                    <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>
                ))}
                {itineraries && itineraries.length === 0 && (
                     <div className="p-4 text-sm text-center text-muted-foreground">
                        No itineraries found. <br/>
                        <Button asChild variant="link" size="sm" className="h-auto p-0">
                            <Link to="/itinerary/create">Create one now?</Link>
                        </Button>
                     </div>
                )}
              </SelectContent>
            </Select>
          </div>
          {selectedItinerary && (
             <div className="space-y-2">
                <label className="text-sm font-medium">Day</label>
                <Select
                    value={selectedDay?.toString() || ''}
                    onValueChange={day => setSelectedDay(Number(day))}
                >
                <SelectTrigger>
                    <SelectValue placeholder="Select a day..." />
                </SelectTrigger>
                <SelectContent>
                    {selectedItinerary.generatedItinerary.days.map(day => (
                        <SelectItem key={day.day} value={day.day.toString()}>Day {day.day}: {day.title}</SelectItem>
                    ))}
                </SelectContent>
                </Select>
          </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={isSaving || !selectedItineraryId || !selectedDay}>
            {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Add Location
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
